import {
  CheckCircle,
  Cancel,
  MoreVert,
  Add,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
  Snackbar,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";
import TierEditorDialog from "./dialogs/TierEditorDialog";

interface SubscriptionTier {
  id: string;
  tier_name: string;
  tier_type: string;
  tier_description: string;
  display_order: number;
  is_active: boolean;
  is_featured: boolean;
  credit_limit: number;
  seats: number;
  companies: number;
  whitelabel_limit: number;
  pricing: any;
  features: any;
  features_header?: string;
  features_text?: string;
  created_at: string | null;
  updated_at: string | null;
  is_system?: boolean;
  has_configuration?: boolean;
}

export default function WhitelabelSubscriptions({
  whitelabelId,
  currentUser,
  getToken,
  eventListener,
}: {
  whitelabelId: string;
  currentUser: User;
  getToken: Function;
  eventListener?: Function;
}) {
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error"
  >("success");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tierToDelete, setTierToDelete] = useState<SubscriptionTier | null>(
    null
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  useEffect(() => {
    loadTiers();
  }, [whitelabelId]);

  const loadTiers = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/subscription-tiers?include_inactive=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setTiers(response.data.tiers || []);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load subscription tiers");
    } finally {
      setLoading(false);
    }
  };

  const handleEditTier = (tier: SubscriptionTier) => {
    setSelectedTier(tier);
    setEditDialogOpen(true);
    handleCloseMenu(tier.id);
  };

  const handleDeleteTier = (tier: SubscriptionTier) => {
    setTierToDelete(tier);
    setDeleteDialogOpen(true);
    handleCloseMenu(tier.id);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    tierId: string
  ) => {
    setMenuAnchorEl({ ...menuAnchorEl, [tierId]: event.currentTarget });
  };

  const handleCloseMenu = (tierId: string) => {
    setMenuAnchorEl({ ...menuAnchorEl, [tierId]: null });
  };

  const confirmDelete = async () => {
    if (!tierToDelete) return;

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/subscription-tiers/${tierToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSnackbarMessage("Subscription tier deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setTierToDelete(null);
      loadTiers();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || "Failed to delete subscription tier";
      if (eventListener) {
        eventListener({ type: "SET_ERROR", error: errorMessage });
      } else {
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
      setDeleteDialogOpen(false);
      setTierToDelete(null);
    }
  };

  if (loading && tiers.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading subscription tiers...</Typography>
      </Box>
    );
  }

  const hasSystemTiers = tiers.length > 0 && tiers.some((tier) => tier.is_system);

  const handleCreateTier = () => {
    setSelectedTier(null);
    setEditDialogOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h6">Subscription Tiers</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreateTier}>
          Create New Tier
        </Button>
      </Box>

      {hasSystemTiers ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Showing system default subscription tiers. Edit any tier to save a custom
          version to your whitelabel instance.
          <br />
          <strong>Pricing:</strong> Set the annual monthly price (e.g., $99/month when billed annually).
          Monthly billing automatically adds 20% (e.g., $119/month when billed monthly).
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          Manage subscription tiers for your whitelabel instance. You can edit or delete
          existing tiers to customize pricing, features, and limits.
          <br />
          <strong>Pricing:</strong> Set the annual monthly price (e.g., $99/month when billed annually).
          Monthly billing automatically adds 20% (e.g., $119/month when billed monthly).
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {tiers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            Loading Subscription Tiers...
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {tiers
            .sort((a, b) => a.display_order - b.display_order)
            .map((tier) => (
              <Grid item xs={12} md={6} lg={4} key={tier.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    opacity: tier.is_active ? 1 : 0.6,
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="h6">{tier.tier_name}</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                          <Chip
                            label={tier.tier_type}
                            size="small"
                          />
                          {tier.is_system && (
                            <Chip
                              label="System Default"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                          {tier.is_featured && (
                            <Chip
                              label="Featured"
                              size="small"
                              color="primary"
                            />
                          )}
                        </Box>
                      </Box>
                      <Box>
                        {tier.is_active ? (
                          <CheckCircle color="success" />
                        ) : (
                          <Cancel color="disabled" />
                        )}
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {tier.tier_description}
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Credits/month
                        </Typography>
                        <Typography variant="body2">
                          {tier.credit_limit.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Seats
                        </Typography>
                        <Typography variant="body2">{tier.seats}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Companies
                        </Typography>
                        <Typography variant="body2">
                          {tier.companies}
                        </Typography>
                      </Box>
                    </Box>

                    {tier.pricing?.annual?.price && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" color="primary">
                          ${tier.pricing.annual.price}/mo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Annual billing
                        </Typography>
                        {tier.pricing?.monthly?.price && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            ${tier.pricing.monthly.price}/mo (monthly billing)
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>

                  <Box
                    sx={{
                      p: 2,
                      pt: 0,
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenMenu(e, tier.id)}
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchorEl[tier.id]}
                      open={Boolean(menuAnchorEl[tier.id])}
                      onClose={() => handleCloseMenu(tier.id)}
                    >
                      <MenuItem onClick={() => handleEditTier(tier)}>
                        Edit
                      </MenuItem>
                      {!tier.is_system && (
                        <MenuItem onClick={() => handleDeleteTier(tier)}>
                          Delete
                        </MenuItem>
                      )}
                    </Menu>
                  </Box>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Subscription Tier</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the "{tierToDelete?.tier_name}" subscription
            tier? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Edit Tier Dialog */}
      <TierEditorDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTier(null);
        }}
        tier={selectedTier}
        whitelabelId={whitelabelId}
        getToken={getToken}
        onSave={() => {
          setSnackbarMessage("Subscription tier updated successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          loadTiers();
        }}
        eventListener={(event) => {
          if (event.type === "SET_ERROR" && event.error) {
            setSnackbarMessage(event.error);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          } else if (event.type === "SET_SUCCESS" && event.message) {
            setSnackbarMessage(event.message);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
          }
        }}
      />
    </Box>
  );
}
